import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    if (saved === 'tr') return 'ar';
    return saved || 'ar';
  });

  useEffect(() => {
    // Update document direction and language
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'ar' ? 'en' : 'ar'));
  };

  const translations = {
    en: {
      nav: {
        home: 'Home',
        portfolio: 'Services',
        about: 'About',
        contact: 'Contact',
        demo: 'Programming Demo',
        masarTech: 'MASAR TECH',
      },
      hero: {
        title: 'Intelligent paths across',
        titleHighlight1: 'Software',
        titleHighlight2: 'Vehicle services & mobility',
        titleHighlight3: 'AI & cloud',
        tagline: 'MASAR is a multi-sector technology ecosystem for the Gulf and Saudi Arabia — software, logistics, vehicle services, mobility platforms, and enterprise-grade engineering in one modular stack.',
        cta: 'Contact us'
      },
      featuredProject: {
        badge: 'Reference build',
        title: 'Academy Management System Demo',
        description: 'An Arabic-first example with multi-branch workflows, payments, and notifications — representative of how MASAR ships serious software for regional operators.',
        viewDemo: 'Open live demo',
        learnMore: 'Explore solutions',
      },
      forAcademies: {
        badge: 'Our Featured Project',
        heroTitle1: 'Manage Your',
        heroTitle2: 'Sports Academy',
        heroTitle3: 'From One Place',
        heroDesc: 'A complete platform for multi-branch sports academies in Saudi Arabia. Registration, payments, attendance, evaluations, and parent communication—all in one system.',
        watchDemo: 'Watch Demo',
        requestDemo: 'Request Demo',
        videoBadge: '1-Minute Demo',
        videoTitle: 'See the platform in action',
        featuresBadge: 'Features',
        featuresTitle: 'Everything You Need',
        contactBadge: 'Get Started',
        contactTitle: 'Request a Demo',
        contactDesc: "Fill the form and we'll contact you within 24 hours.",
        form: {
          academyName: 'Academy Name',
          contactName: 'Contact Name',
          phone: 'Phone Number',
          email: 'Email',
          message: 'Message (optional)',
          submit: 'Send Request',
          whatsapp: 'WhatsApp',
        },
      },
      projects: {
        title: 'Our Services',
        subtitle: 'Explore our latest projects and solutions',
        loading: 'Loading projects...',
        items: [
          {
            title: 'Software & digital systems',
            description: 'Enterprise web, APIs, and integrations with clean architecture and long-term maintainability.'
          },
          {
            title: 'SaaS & platforms',
            description: 'Multi-tenant products, dashboards, and role-based experiences built for scale.'
          },
          {
            title: 'Workflow automation',
            description: 'Orchestrated processes across teams and systems with measurable operational gains.'
          },
          {
            title: 'Smart logistics & operations',
            description: 'Visibility across fleet, branches, and fulfillment with data-backed decisions.'
          },
          {
            title: 'Vehicle services & mobility',
            description: 'Trading, fleet and logistics fleets, driver apps, dealership systems, MaaS, vehicle marketplaces, and connected mobility — engineered for operators in the region.'
          },
          {
            title: 'AI, cloud & enterprise systems',
            description: 'Modern cloud foundations with pragmatic AI embedded where it creates leverage.'
          }
        ]
      },
      about: {
        title: 'About MASAR',
        description1: 'MASAR | مسار is a technology ecosystem company anchored in the Gulf and especially Saudi Arabia — building intelligent paths between software, operations, movement, and data.',
        description2: 'We focus on scalable digital systems, SaaS platforms, workflow automation, logistics, vehicle services and mobility (fleet, trading, operations), and enterprise-grade cloud and AI solutions.',
        description3: 'Across MASAR Software, Logistics, Mobility, AI, Cloud, Systems, and Labs, one visual and engineering DNA keeps every product coherent:',
        bullet1: 'Modular architecture you can extend without rework',
        bullet2: 'Premium, minimal interfaces with strong typography and spacing',
        bullet3: 'Operational intelligence — dashboards and signals operators can trust',
        description4: 'Whether you run a national-scale logistics network or a fast-growing digital product, we engineer for reliability, compliance-minded workflows, and measurable outcomes.',
        strengths: {
          design: {
            title: 'Unified ecosystem',
            desc: 'One design language from marketing site to product surfaces'
          },
          delivery: {
            title: 'Engineering velocity',
            desc: 'Ship in milestones with clear ownership and quality gates'
          },
          automation: {
            title: 'Process automation',
            desc: 'Remove friction between departments and core systems'
          },
          bots: {
            title: 'AI & data',
            desc: 'Pragmatic models, analytics, and automation where ROI is clear'
          },
          webdev: {
            title: 'Software & SaaS',
            desc: 'Modern stacks, performance budgets, and maintainable codebases'
          },
          growth: {
            title: 'Gulf, KSA & vehicle services',
            desc: 'Arabic-first UX with regional integrations — plus fleet, trading, and mobility platforms for enterprises on the ground'
          }
        }
      },
      contact: {
        title: 'Get in touch',
        subtitle: 'Partnerships, product inquiries, and enterprise discussions',
        email: 'Email',
        phone: 'Phone',
        follow: 'Follow Us',
        form: {
          name: 'Name',
          namePlaceholder: 'Your name',
          email: 'Email',
          emailPlaceholder: 'your.email@example.com',
          message: 'Message',
          messagePlaceholder: 'Tell us about your project...',
          send: 'Send Message',
          sending: 'Sending...',
          success: 'Thank you for your message! We will get back to you soon.',
          error: 'Failed to send message. Please try again.'
        }
      },
      footer: {
        rights: 'All rights reserved.'
      }
    },
    ar: {
      nav: {
        home: 'الرئيسية',
        portfolio: 'الخدمات',
        about: 'من نحن',
        contact: 'اتصل بنا',
        demo: 'نموذج برمجي',
        masarTech: 'مسار للتقنية',
      },
      hero: {
        title: 'مسارات ذكية عبر',
        titleHighlight1: 'البرمجيات',
        titleHighlight2: 'خدمات المركبات والتنقل',
        titleHighlight3: 'الذكاء والسحابة',
        tagline: 'MASAR | مسار منظومة تقنية متعددة القطاعات بتركيز على الخليج والمملكة — برمجيات، لوجستيات، خدمات مركبات ومنصات تنقل، وهندسة مؤسساتية في منظومة واحدة.',
        cta: 'تواصل معنا'
      },
      featuredProject: {
        badge: 'نموذج مرجعي',
        title: 'نظام إدارة الأكاديميات الرياضية',
        description: 'مثال عملي لتجربة عربية أولاً، فروع متعددة، مدفوعات وإشعارات — يعبّر عن أسلوب MASAR في بناء برمجيات جادة لمشغّلين في المنطقة.',
        viewDemo: 'فتح العرض الحي',
        learnMore: 'استكشف الخدمات',
      },
      forAcademies: {
        badge: 'أبرز المشاريع',
        heroTitle1: 'أدر',
        heroTitle2: 'أكاديميتك الرياضية',
        heroTitle3: 'من مكان واحد',
        heroDesc: 'منصة متكاملة لأكاديميات رياضية متعددة الفروع في المملكة. التسجيل، المدفوعات، الحضور، التقييمات، والتواصل مع أولياء الأمور—كل ذلك في نظام واحد.',
        watchDemo: 'شاهد العرض',
        requestDemo: 'اطلب عرضاً',
        videoBadge: 'عرض دقيقة واحدة',
        videoTitle: 'شاهد المنصة في العمل',
        featuresBadge: 'المميزات',
        featuresTitle: 'كل ما تحتاجه',
        contactBadge: 'ابدأ الآن',
        contactTitle: 'اطلب عرضاً توضيحياً',
        contactDesc: 'املأ النموذج وسنتواصل معك خلال 24 ساعة.',
        form: {
          academyName: 'اسم الأكاديمية',
          contactName: 'اسم المسؤول',
          phone: 'رقم الجوال',
          email: 'البريد الإلكتروني',
          message: 'رسالة (اختياري)',
          submit: 'إرسال الطلب',
          whatsapp: 'واتساب',
        },
      },
      projects: {
        title: 'خدماتنا',
        subtitle: 'استكشف أحدث مشاريعنا وحلولنا',
        loading: 'جاري تحميل المشاريع...',
        items: [
          {
            title: 'البرمجيات والأنظمة الرقمية',
            description: 'ويب مؤسسي، واجهات برمجة، وتكاملات بمعمارية نظيفة وقابلة للصيانة على المدى الطويل.'
          },
          {
            title: 'SaaS والمنصات',
            description: 'منتجات متعددة المستأجرين، لوحات تحكم، وتجارب صلاحيات تنمو مع أعمالك.'
          },
          {
            title: 'أتمتة سير العمل',
            description: 'تنسيق العمليات بين الفرق والأنظمة مع مؤشرات أداء تشغيلية واضحة.'
          },
          {
            title: 'الوجستيات والتشغيل الذكي',
            description: 'رؤية للأسطول والفروع والتسليم مع قرارات مبنية على البيانات.'
          },
          {
            title: 'خدمات المركبات والتنقل',
            description: 'تجارة المركبات، أساطيل الشحن والوجستيات، تطبيقات السائقين، أنظمة المعارض، التنقل كخدمة، أسواق المركبات، والتنقل المتصل — بهندسة للمشغّلين في المنطقة.'
          },
          {
            title: 'الذكاء الاصطناعي والسحابة والأنظمة المؤسسية',
            description: 'بنية سحابية حديثة مع ذكاء اصطناعي عملي حيث يخلق قيمة حقيقية.'
          }
        ]
      },
      about: {
        title: 'عن مسار',
        description1: 'MASAR | مسار شركة منظومة تقنية بتركيز على الخليج وخاصة المملكة العربية السعودية — نبني مسارات ذكية بين البرمجيات والتشغيل والحركة والبيانات.',
        description2: 'نعمل على أنظمة رقمية قابلة للتوسع، منصات SaaS، أتمتة سير العمل، الوجستيات، خدمات المركبات والتنقل (أساطيل، تجارة، تشغيل)، وحلول سحابية ومؤسسية مدعومة بالذكاء الاصطناعي.',
        description3: 'عبر وحدات MASAR Software وLogistics وMobility وAI وCloud وSystems وLabs يبقى  البصري والهندسي واحداً:',
        bullet1: 'معمارية معيارية يمكن توسيعها دون إعادة اختراع العجلة',
        bullet2: 'واجهات بسيطة وفاخرة مع تسلسل هجري قوي للنصوص والمسافات',
        bullet3: 'ذكاء تشغيلي — لوحات ومؤشرات يثق بها المشغّلون',
        description4: 'سواء كنت تدير شبكة لوجستيات وطنية أو منتجاً رقمياً سريع النمو، نهندس للموثوقية ولسلاسة الامتثال وللنتائج القابلة للقياس.',
        strengths: {
          design: {
            title: 'منظومة موحّدة',
            desc: 'لغة تصميم واحدة من الموقع إلى واجهات المنتج'
          },
          delivery: {
            title: 'سرعة هندسية منضبطة',
            desc: 'إطلاق على مراحل مع ملكية واضحة وبوابات جودة'
          },
          automation: {
            title: 'أتمتة العمليات',
            desc: 'تقليل الاحتكاك بين الأقسام والأنظمة الأساسية'
          },
          bots: {
            title: 'الذكاء والبيانات',
            desc: 'نماذج وتحليلات وأتمتة حيث تتضح العائد'
          },
          webdev: {
            title: 'البرمجيات وSaaS',
            desc: 'تقنيات حديثة وأداء عالٍ وقواعد شيفرة قابلة للصيانة'
          },
          growth: {
            title: 'الخليج والمملكة وخدمات المركبات',
            desc: 'عربية أولاً وتكاملات محلية — مع منصات أساطيل وتجارة وتنقل للمؤسسات على الأرض'
          }
        }
      },
      contact: {
        title: 'تواصل معنا',
        subtitle: 'الشراكات، استفسارات المنتج، ومناقشات المؤسسات',
        email: 'البريد الإلكتروني',
        phone: 'الهاتف',
        follow: 'تابعنا',
        form: {
          name: 'الاسم',
          namePlaceholder: 'اسمك',
          email: 'البريد الإلكتروني',
          emailPlaceholder: 'بريدك@example.com',
          message: 'الرسالة',
          messagePlaceholder: 'أخبرنا عن مشروعك...',
          send: 'إرسال الرسالة',
          sending: 'جاري الإرسال...',
          success: 'شكراً لرسالتك! سنعود إليك قريباً.',
          error: 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.'
        }
      },
      footer: {
        rights: 'جميع الحقوق محفوظة.'
      }
    },
    tr: {
      nav: {
        home: 'Ana Sayfa',
        portfolio: 'Portföy',
        about: 'Hakkımızda',
        contact: 'İletişim',
        demo: 'Akademi Demosu',
        masarTech: 'MASAR TECH',
      },
      hero: {
        title: 'Biz İnşa Ediyoruz',
        titleHighlight1: 'Web Siteleri',
        titleHighlight2: 'Botlar',
        titleHighlight3: 'Otomasyonlar',
        tagline: 'Dijital varlığınızı, büyümeyi ve verimliliği artıran son teknoloji web çözümleri, akıllı AI botları ve sorunsuz iş akışı otomasyonlarıyla dönüştürüyoruz.',
        cta: 'Teklif Al'
      },
      featuredProject: {
        badge: 'Öne Çıkan Proje',
        title: 'Spor Akademisi Yönetim Sistemi',
        description: 'Çok şubeli destek, online ödemeler (Mada, STC Pay, Apple Pay), SMS bildirimleri, devam takibi ve Suudi pazarı için tam Arapça/RTL desteği ile spor akademileri için kapsamlı bir yönetim platformu.',
        viewDemo: 'Canlı Demoyu Görüntüle',
        learnMore: 'Daha Fazla Bilgi',
      },
      forAcademies: {
        badge: 'Akademi Sahipleri İçin',
        heroTitle1: 'Yönetin',
        heroTitle2: 'Spor Akademinizi',
        heroTitle3: 'Tek Yerden',
        heroDesc: 'Suudi Arabistan\'da çok şubeli spor akademileri için eksiksiz bir platform. Kayıt, ödemeler, devam, değerlendirmeler ve veli iletişimi—hepsi tek bir sistemde.',
        watchDemo: 'Demoyu İzle',
        requestDemo: 'Demo Talep Et',
        videoBadge: '1 Dakikalık Demo',
        videoTitle: 'Platformu çalışırken görün',
        featuresBadge: 'Özellikler',
        featuresTitle: 'İhtiyacınız Olan Her Şey',
        contactBadge: 'Başlayın',
        contactTitle: 'Demo Talep Edin',
        contactDesc: 'Formu doldurun, 24 saat içinde sizinle iletişime geçeceğiz.',
        form: {
          academyName: 'Akademi Adı',
          contactName: 'İletişim Adı',
          phone: 'Telefon Numarası',
          email: 'E-posta',
          message: 'Mesaj (isteğe bağlı)',
          submit: 'Talep Gönder',
          whatsapp: 'WhatsApp',
        },
      },
      projects: {
        title: 'Hizmetlerimiz',
        subtitle: 'En son projelerimizi ve çözümlerimizi keşfedin',
        loading: 'Projeler yükleniyor...',
        items: [
          {
            title: 'Web sitesi oluşturma',
            description: 'Modern tasarım ve duyarlı düzenlerle profesyonel web sitesi geliştirme.'
          },
          {
            title: 'Müşteri Destek Botu',
            description: 'Doğal dil işleme ile 7/24 müşteri sorgularını yöneten akıllı sohbet botu.'
          },
          {
            title: 'İş Akışı Otomasyon Sistemi',
            description: 'Otomatik görev yönetimi ve raporlama ile sadeleştirilmiş iş süreçleri.'
          },
          {
            title: 'SaaS Kontrol Paneli',
            description: 'Gerçek zamanlı veri görselleştirme ve içgörülerle güzel analitik kontrol paneli.'
          },
          {
            title: 'E-Ticaret Platformu',
            description: 'AI destekli öneriler ve otomatik envanter yönetimi ile modern e-ticaret çözümü.'
          },
          {
            title: 'Mobil Uygulamalar',
            // description: 'iOS ve Android cihazlarda çalışan, iş ihtiyaçlarınızı karşılamak için özel olarak tasarlanmış, sorunsuz bir kullanıcı deneyimi ve güçlü temel işlevlerle basit ve pratik mobil uygulamalar geliştiriyoruz.'
            description: "Android ve iOS için hızlı ve pratik mobil uygulamalar geliştiriyoruz."
          }
        ]
      },
      about: {
        title: 'Hakkımızda',
        description1: 'Dijital çağda işletmelerin gelişmesine yardımcı olan gelişmiş teknik çözümler yaratmaya tutkulu bir dijital hizmetler şirketiyiz.',
        description2: 'Web geliştirme, yapay zeka ve akıllı otomasyon konularındaki deneyimimizle, iş ihtiyaçlarınızı karşılamak ve ticari hedeflerinize ulaşmak için özel olarak tasarlanmış çözümler sunuyoruz.',
        description3: 'Ekibimiz şunları inşa etmek için yaratıcı tasarım ve teknik uzmanlığı birleştiriyor:',
        bullet1: 'Profesyonel web siteleri',
        bullet2: 'Akıllı AI destekli botlar',
        bullet3: 'Süreçleri hızlandırmak ve manuel çabayı azaltmak için iş akışı otomasyonu',
        description4: 'Sadece harika görünen çözümler tasarlamıyoruz, aynı zamanda gerçek iş sonuçları elde ediyor ve organizasyonunuzun büyümesini ve genişlemesini kolaylaştırıyoruz.',
        strengths: {
          design: {
            title: 'Yaratıcı Tasarım',
            desc: 'Büyüleyen ve dönüştüren güzel, modern tasarımlar'
          },
          delivery: {
            title: 'Hızlı Teslimat',
            desc: 'Kaliteden ödün vermeden hızlı teslimat süreleri'
          },
          automation: {
            title: 'Otomasyon Uzmanlığı',
            desc: 'İş akışlarını sadeleştirin ve verimliliği artırın'
          },
          bots: {
            title: 'AI Botları',
            desc: 'Müşteri deneyimini geliştiren akıllı sohbet botları'
          },
          webdev: {
            title: 'Web Geliştirme',
            desc: 'En son teknolojilerle inşa edilmiş ölçeklenebilir, duyarlı web siteleri'
          },
          growth: {
            title: 'Büyüme Odaklı',
            desc: 'İş büyümesini artırmak için tasarlanmış çözümler'
          }
        }
      },
      contact: {
        title: 'İletişime Geçin',
        subtitle: 'Bir sonraki projenizi tartışalım',
        email: 'E-posta',
        phone: 'Telefon',
        follow: 'Bizi Takip Edin',
        form: {
          name: 'İsim',
          namePlaceholder: 'Adınız',
          email: 'E-posta',
          emailPlaceholder: 'e-posta@example.com',
          message: 'Mesaj',
          messagePlaceholder: 'Projeniz hakkında bize bilgi verin...',
          send: 'Mesaj Gönder',
          sending: 'Gönderiliyor...',
          success: 'Mesajınız için teşekkürler! En kısa sürede size dönüş yapacağız.',
          error: 'Mesaj gönderilemedi. Lütfen tekrar deneyin.'
        }
      },
      footer: {
        rights: 'Tüm hakları saklıdır.'
      }
    }
  };

  const t = translations[language];

  const setLanguageDirect = (lang) => {
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, setLanguage: setLanguageDirect, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

